import { css, keyframes } from '@emotion/react'
import { SxProps, Theme } from '@mui/material'

import CustomPalette from '@/theme/CustomPalette'
import { marginPresets, paddingPresets } from '@/theme/responsive'

const shake = keyframes`
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }
  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }
  30%, 50%, 70% {
    transform: translate3d(-4px, 0, 0);
  }
  40%, 60% {
    transform: translate3d(4px, 0, 0);
  }`

export const errorMixin = css`
  animation: ${shake} 600ms ease;
`

export const WrapperBoxStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  overflow: 'hidden',
  backgroundColor: '#F5F5F5',
}

export const ContainerBoxStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  flexGrow: 1,
  alignItems: 'center',
  px: paddingPresets.page,
  pt: { xs: 3, sm: 4, md: 5 },
  pb: { xs: 4, sm: 6, md: 8 },
  backgroundColor: '#F5F5F5',
}

export const TypographyH4Style: SxProps<Theme> = {
  fontFamily: 'Beausite Fit',
  fontWeight: 500,
  color: '#1A1A1A',
  mt: { xs: 0, lg: 0 },
  mb: marginPresets.small,
  fontSize: { xs: '20px', sm: '22px', lg: '28px' },
  lineHeight: { xs: 1.2, lg: 1.3 },
  maxWidth: { xs: '100%', lg: '350px', xl: '100%' },
  wordBreak: 'break-word',
  textAlign: 'left',
  '@media (min-width: 1440px)': {
    maxWidth: '100%',
  },
}

export const TypographySpanStyle: SxProps<Theme> = {
  color: '#1A1A1A',
  fontFamily: 'Beausite Fit',
  fontWeight: 500,
  display: { xs: 'inline', sm: 'block' },
  whiteSpace: { xs: 'normal', sm: 'nowrap' },
}

export const TypographyEmailStyle: SxProps<Theme> = {
  color: CustomPalette.SecondaryNightBlue900,
  fontWeight: 390,
  fontSize: { xs: '13px', sm: '14px', lg: '16px' },
  lineHeight: { xs: 1.4, lg: 1.5 },
  mb: { xs: 3, lg: 4 },
  textAlign: 'left',
  px: 0,
  mx: 0,
}

export const FormControlStyle = (errors: boolean): SxProps<Theme> => ({
  mb: { xs: 2, lg: 2 },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: CustomPalette.SecondaryNightBlue600,
      borderRadius: { xs: '8px', lg: '10px' },
    },
    '&.Mui-focused fieldset': {
      borderColor: CustomPalette.PilotBlue,
    },
  },
  '& .MuiOutlinedInput-input': {
    backgroundColor: errors ? 'pink' : CustomPalette.white,
    fontSize: { xs: '13px', sm: '14px', lg: '16px' },
    py: { xs: 1.5, lg: 2 },
  },
})

export const InputLabelStyle: SxProps<Theme> = {
  '&.Mui-focused': { color: CustomPalette.PilotBlue },
  color: CustomPalette.SecondaryNightBlue900,
  fontSize: { xs: '13px', sm: '14px', lg: '16px' },
  margin: { xs: '0 0 -8px -12px', lg: '0 0 -10px -14px' },
  position: 'static',
}

export const InputStyle: SxProps<Theme> = {
  backgroundColor: CustomPalette.white,
  '& .MuiInputBase-input, & .MuiOutlinedInput-input': {
    '::placeholder': {
      color: CustomPalette.SecondaryNightBlue600,
      fontSize: { xs: '13px', sm: '14px', lg: '16px' },
      fontFamily: '"Public Sans", sans-serif',
      fontWeight: '400',
    },
    '::-webkit-input-placeholder': {
      color: CustomPalette.SecondaryNightBlue600,
      fontSize: { xs: '13px', sm: '14px', lg: '16px' },
      fontFamily: '"Public Sans", sans-serif',
      fontWeight: '400',
    },
    ':-ms-input-placeholder': {
      color: CustomPalette.SecondaryNightBlue600,
      fontSize: { xs: '13px', sm: '14px', lg: '16px' },
      fontFamily: '"Public Sans", sans-serif',
      fontWeight: '400',
    },
  },
}

export const MailIconStyle: SxProps<Theme> = {
  color: CustomPalette.SecondaryNightBlue600,
  backgroundColor: CustomPalette.white,
  fontSize: { xs: '20px', lg: '23px' },
  mb: { xs: -1.5, lg: -2 },
  pb: { xs: 2, lg: 2.25 },
}

export const FooterBoxStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pb: { xs: 3, lg: 4 },
  px: paddingPresets.page,
}

export const LoginContainerStyle: SxProps<Theme> = {
  width: '100%',
  maxWidth: { xs: '400px', sm: '450px', md: '536px', lg: '490px' },
  px: { xs: 1.5, sm: 3, md: 0, lg: 0 },
  mx: 'auto',
  '@media (min-width: 1023px)': {
    maxWidth: '490px',
  },
}

export const LoginGridContainerStyle: SxProps<Theme> = {
  width: '100%',
  px: { xs: 0, sm: 0, md: 4, lg: 0 },
  '@media (min-width: 1023px)': {
    px: 0,
  },
}

export const LogoWrapperBoxStyle: SxProps<Theme> = {
  '@media (min-width: 1023px)': {
    ml: 12,
  },
  '@media (min-width: 1200px)': {
    ml: 5,
  },
}

export const LogoOuterBoxStyle: SxProps<Theme> = {
  width: { xs: '100%', md: '100%', lg: '460px' },
  display: 'flex',
  justifyContent: { xs: 'center', md: 'center', lg: 'center' },
  mb: { xs: 6, md: 6, lg: 8 },
  mt: { xs: 0, md: 0, lg: 0 },
  '@media (min-width: 1023px)': {
    width: '460px',
    justifyContent: 'flex-start',
  },
}

export const LogoContainerBoxStyle: SxProps<Theme> = {
  width: { xs: '80px', md: '80px', lg: '200px' },
  height: { xs: '20px', md: '20px', lg: '50px' },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '@media (min-width: 1023px)': {
    width: '200px',
    height: '50px',
  },
  '& svg, & img': {
    width: '100%',
    height: 'auto',
    maxHeight: '100%',
  },
}

export const FormWrapperBoxStyle: SxProps<Theme> = {
  width: { xs: '100%', sm: '100%', md: '450px', lg: '460px', xl: '600px' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  '@media (min-width: 1023px)': {
    width: '460px',
  },
  '@media (min-width: 1440px)': {
    width: '600px',
  },
}

export const WelcomeTextStyle: SxProps<Theme> = {
  color: CustomPalette.PilotBlue,
}

export const ResponsiveLineBreakStyle: SxProps<Theme> = {
  display: 'block',
}

export const ResponsiveLineBreak320OnlyStyle: SxProps<Theme> = {
  display: 'block',
  '@media (min-width: 375px)': {
    display: 'none',
  },
}

export const FooterContainerStyle: SxProps<Theme> = {
  width: '100%',
  maxWidth: { xs: '400px', sm: '450px', md: '536px', lg: '490px' },
  px: { xs: 1.5, sm: 3, md: 0, lg: 0 },
  mx: 'auto',
  '@media (min-width: 1023px)': {
    maxWidth: '490px',
    px: 0,
  },
}
